import getAMBClient from './getAMBClient';
import {createSubscriptionId, createRegistryId, replaceChannelParameters} from './utils';
import {get, defer} from '@servicenow/ui-internal';
import createGarbageCollectorMap from './createGarbageCollectorMap';

const ambChannelRegistry = createGarbageCollectorMap((registryId, channelListener) => {
	channelListener.unsubscribe(channelListener.getCallback());
});

function createSubscriptionCallback(dispatch, successActionType, errorActionType, actionMetaData) {
	// use setTimeout (defer) because of an AMB client subscription timing issue
	return response =>
		defer(() => {
			if (response.successful) {
				dispatch(successActionType, {response}, actionMetaData);
			} else {
				const {error} = response;
				dispatch(errorActionType, error, actionMetaData, true);
			}
		});
}

function createSubscriptionOptionsCallback(options) {
	return () => options || {};
}

export const AMB_CHANNEL_SUBSCRIBE_STARTED = 'AMB_CHANNEL_SUBSCRIBE_STARTED';
export const AMB_CHANNEL_SUBSCRIBE_SUCCEEDED = 'AMB_CHANNEL_SUBSCRIBE_SUCCEEDED';
export const AMB_CHANNEL_SUBSCRIBE_FAILED = 'AMB_CHANNEL_SUBSCRIBE_FAILED';
export const AMB_CHANNEL_UNSUBSCRIBE_SUCCEEDED = 'AMB_CHANNEL_UNSUBSCRIBE_SUCCEEDED';
export const AMB_MESSAGE_RECEIVED = 'AMB_MESSAGE_RECEIVED';

export function ambSubscriptionEffect(
	ambClient,
	subscriptionId,
	ambChannelRegistry,
	channelId,
	options = {},
	coeffects
) {
	const {
		subscribeStartedActionType = AMB_CHANNEL_SUBSCRIBE_STARTED,
		subscribeSucceededActionType = AMB_CHANNEL_SUBSCRIBE_SUCCEEDED,
		subscribeFailedActionType = AMB_CHANNEL_SUBSCRIBE_FAILED,
		unsubscribeSucceededActionType = AMB_CHANNEL_UNSUBSCRIBE_SUCCEEDED,
		messageReceivedActionType = AMB_MESSAGE_RECEIVED,
		encodeURIComponentForChannelId = true,
		subscriptionOptions = {}
	} = options;
	const {
		action,
		action: {
			meta: {id: containerId}
		},
		dispatch,
		host
	} = coeffects;
	const registryId = createRegistryId(containerId, subscriptionId);
	const resolvedChannel = replaceChannelParameters(
		channelId,
		action.payload,
		encodeURIComponentForChannelId
	);
	const metaData = {...action.meta, channelId: resolvedChannel};
	const shouldSubscribe = get(action, 'payload.subscribe', false);
	const subscriptionCallback = createSubscriptionCallback(
		dispatch,
		subscribeSucceededActionType,
		subscribeFailedActionType,
		metaData
	);
	const subscribeOptionsCallback = createSubscriptionOptionsCallback(subscriptionOptions);

	if (!ambChannelRegistry.has(registryId)) {
		ambChannelRegistry.set(
			registryId,
			ambClient.getChannel(resolvedChannel, {
				subscriptionCallback,
				subscribeOptionsCallback
			}),
			host
		);
	}

	let channelListener = ambChannelRegistry.get(registryId);
	const channelIdChanged = resolvedChannel !== channelListener.getName();
	if (channelIdChanged) {
		ambChannelRegistry.set(
			registryId,
			ambClient.getChannel(resolvedChannel, {
				subscriptionCallback,
				subscribeOptionsCallback
			}),
			host
		);
		channelListener = ambChannelRegistry.get(registryId);
	}

	const isSubscribed = channelListener.getCallback() !== undefined;
	const listenerFn = message => {
		dispatch(messageReceivedActionType, message, metaData);
	};
	if (!isSubscribed && shouldSubscribe) {
		dispatch(subscribeStartedActionType, {}, metaData);
		channelListener.subscribe(listenerFn);
	} else if (isSubscribed && !shouldSubscribe) {
		ambChannelRegistry.delete(registryId);
		channelListener.unsubscribe(channelListener.getCallback());
		dispatch(unsubscribeSucceededActionType, {}, metaData);
	}
}

/**
 * Effect to manage an an amb channel subscription.
 *
 * @function createAmbSubscriptionEffect
 * @example
 * import {createAmbSubscriptionEffect} from '@servicenow/ui-effect-amb';
 *
 * const recordWatcherHandler = createAmbSubscriptionEffect('/rw/default/:table/:filter', {
 *      subscribeStartedActionType: 'RECORD_WATCHER_SUBSCRIBE_STARTED',
 *      subscribeSucceededActionType: 'RECORD_WATCHER_SUBSCRIBED',
 *      subscribeFailedActionType: 'RECORD_WATCHER_SUBSCRIBE_FAILED',
 *      unsubscribeSucceededActionType: 'RECORD_WATCHER_UNSUBSCRIBED',
 *      messageReceivedActionType: 'RECORD_WATCHER_ITEM_CHANGED',
 *      subscriptionOptions: {foo: 'bar'}
 * });
 *
 * const recordItemComponent = createCustomElement('sn-record-item', {
 *      renderer: {
 *          view: (state, dispatch) => {
 *              const {table, filter} = state.properties;
 *              function subscribe() {
 *                  dispatch('RECORD_WATCHER_SUBSCRIPTION_CHANGED', {
 *                      table,
 *                      filter,
 *                      subscribe: true
 *                  });
 *              }
 *
 *              function unsubscribe() {
 *                  dispatch('RECORD_WATCHER_SUBSCRIPTION_CHANGED', {
 *                      table,
 *                      filter,
 *                      subscribe: false
 *                  });
 *              }
 *
 *              return (
 *                  <div>
 *                      <button on-click='subscribe'>Subscribe</button>
 *                      <button on-click='unsubscribe'>Unsubscribe</button>
 *                  </div>
 *              );
 *          }
 *      },
 *	    properties: {
 *          table: {},
 *          filter: {}
 *	    },
 *      actionHandlers: {
 *		    'RECORD_WATCHER_SUBSCRIPTION_CHANGED': recordWatcherHandler
 *		}
 * });
 *
 * @param {String} channelId - AMB Channel ID
 * @param {Object} options
 * @param {String} options.subscribeStartedActionType - Action dispatched when an amb subscription is started
 * @param {String} options.subscribeSucceededActionType - Action dispatched if an amb subscription succeeds
 * @param {String} options.subscribeFailedActionType - Action dispatched if an amb subscription fails
 * @param {String} options.unsubscribeSucceededActionType - Action dispatched when an amb unsubscribe is successful
 * @param {String} options.messageReceivedActionType - Action dispatched when a message is received on a channel
 * @param {Boolean} options.encodeURIComponentForChannelId - Encode channel id URL (default: true)
 * @param {Object} options.subscriptionOptions - Arbitary key/value pass through object (default: {})
 *
 * @return {Object} AMB Subscription Effect Descriptor
 */
export default function createAmbSubscriptionEffect(channelId, options) {
	return {
		effect: ambSubscriptionEffect,
		args: [getAMBClient(), createSubscriptionId(), ambChannelRegistry, channelId, options]
	};
}
